import { AddressSearchResponse } from '@application/components/address-search/AddressSearch';
import { ErrorSnackbar } from '@application/components';
import { FeedbackContext, UserContext } from '@application/contexts';
import { extractErrorCodes } from '@application/utils/urql-utils';
import { GetSitesResponse, SiteDataView } from '@domain/graphql.types';
import { useLazySitesQuery } from '@domain/index';
import { CombinedError } from 'urql';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultSearchFilters, SearchFilters, toSearchFiltersInput } from '../filters/searchFilters';
import { SiteLookupDataView } from '../siteLookupDataView';

/** Everything a search depends on: a response belongs to exactly one request. */
export type SiteSearchRequest = {
  address: AddressSearchResponse;
  radius: number;
  filters: SearchFilters;
  dataView: SiteLookupDataView;
};

export type SiteSearchResult = {
  request: SiteSearchRequest;
  data: GetSitesResponse;
};

export type SiteSearchOutcome = 'success' | 'error' | 'superseded';

const hasLocation = (address?: AddressSearchResponse): address is AddressSearchResponse => !!address?.fullAddress || !!address?.coordinates;

const isRateLimited = (error: CombinedError) =>
  extractErrorCodes(error).some((e) => e.status === 429) || error.graphQLErrors?.some((e) => e.message.includes('Rate limit exceeded'));

type Props = {
  address?: AddressSearchResponse;
  radius: number;
  dataView: SiteLookupDataView;
};

/**
 * Owns the site search: the applied filters, the displayed result and the pending/failed request.
 * Applied filters only change when a search with them succeeds, and only the latest request may
 * update the state, so a slow response can never overwrite newer criteria.
 */
const useSiteSearch = ({ address, radius, dataView }: Props) => {
  const { t } = useTranslation();
  const { setFeedback } = useContext(FeedbackContext);
  const { refreshUser } = useContext(UserContext);
  const searchSites = useLazySitesQuery();

  const [appliedFilters, setAppliedFilters] = useState<SearchFilters>(defaultSearchFilters);
  const [result, setResult] = useState<SiteSearchResult | null>(null);
  const [pendingRequest, setPendingRequest] = useState<SiteSearchRequest | null>(null);
  const [failedRequest, setFailedRequest] = useState<SiteSearchRequest | null>(null);

  const latestRequestId = useRef(0);
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;

  const search = useCallback(
    async (request: SiteSearchRequest): Promise<SiteSearchOutcome> => {
      const requestId = ++latestRequestId.current;
      setPendingRequest(request);
      setFailedRequest(null);

      const response = await searchSites({
        input: {
          coordinates: request.address.coordinates,
          range: request.radius,
          stateCode: request.address.regionCode || '',
          viewMode: request.dataView === 'historical' ? SiteDataView.Historical : SiteDataView.Realtime,
          filters: toSearchFiltersInput(request.filters),
        },
      });

      if (requestId !== latestRequestId.current) {
        return 'superseded';
      }

      setPendingRequest(null);

      if (response.error || !response.data?.sites) {
        setFailedRequest(request);

        if (response.error && isRateLimited(response.error)) {
          setFeedback(<ErrorSnackbar message={t('siteLookup.error.rateLimitExceeded')} onClose={() => setFeedback(null)} />);
        }

        return 'error';
      }

      setResult({ request, data: response.data.sites });
      setAppliedFilters(request.filters);

      // A completed search consumes one of the tenant's daily searches, so refresh
      // the user to update the remaining searches count shown under the search field.
      refreshUser();

      return 'success';
    },
    [refreshUser, searchSites, setFeedback, t],
  );

  // A new address, radius or view searches again with the applied filters.
  // biome-ignore lint/correctness/useExhaustiveDependencies: search only when the location or the view changes
  useEffect(() => {
    if (!hasLocation(address)) {
      return;
    }

    search({ address, radius, dataView, filters: appliedFiltersRef.current });
  }, [address, radius, dataView]);

  const applyFilters = useCallback(
    async (filters: SearchFilters): Promise<SiteSearchOutcome> => {
      if (!hasLocation(address)) {
        // Nothing to search yet: the filters will be used by the first search.
        setAppliedFilters(filters);
        return 'success';
      }

      return search({ address, radius, dataView, filters });
    },
    [address, dataView, radius, search],
  );

  const retry = useCallback(() => {
    if (failedRequest) {
      search(failedRequest);
    }
  }, [failedRequest, search]);

  return {
    actions: { applyFilters, retry },
    state: { appliedFilters, failedRequest, pendingRequest, result },
  };
};

export default useSiteSearch;
